"use client";

import * as React from "react";
import { motion } from "motion/react";
import { Eye, Lock, Vote, Shield, FileText, Network } from "lucide-react";
import { Section } from "@/components/layout/section";
import { SectionHeader } from "@/components/ui/section-header";

const PILLARS = [
  { name: "Perception", icon: Eye, description: "Narrative engineering, reputation management, and perception proliferation. The capability to shape how the world sees a leader, institution, brand, or nation.", capabilities: ["Narrative engineering", "Sentiment triangulation"], gradient: "from-red/30 to-red/0", accent: "text-red", accentBg: "bg-red/10", angle: 90 },
  { name: "Politics", icon: Vote, description: "Electoral intelligence, voter analytics, and campaign engineering. The capability to understand electorates, engineer narratives, and deliver verified victories.", capabilities: ["Ground-truth intelligence", "Voter analytics"], gradient: "from-emerald/30 to-emerald/0", accent: "text-emerald", accentBg: "bg-emerald/10", angle: 162 },
  { name: "Policy", icon: FileText, description: "eGovernance transformation, citizen engagement, and governance delivery. The capability to convert political authority into measurable public outcomes.", capabilities: ["eGovernance delivery", "Citizen engagement"], gradient: "from-amber-gold/30 to-amber-gold/0", accent: "text-amber-gold", accentBg: "bg-amber-gold/10", angle: 234 },
  { name: "Policing", icon: Shield, description: "Law enforcement intelligence, national security frameworks, and threat operations. The intelligence layer enabling precision response to national threats.", capabilities: ["Threat operations", "National security frameworks"], gradient: "from-coral/30 to-coral/0", accent: "text-coral", accentBg: "bg-coral/10", angle: 306 },
  { name: "Privacy", icon: Lock, description: "Sovereign-grade encryption, zero-trust architecture, and post-quantum security. The difference between operational survival and catastrophe.", capabilities: ["Zero-trust architecture", "Post-quantum cryptography"], gradient: "from-purple/30 to-purple/0", accent: "text-purple", accentBg: "bg-purple/10", angle: 18 },
];

export function PentaPsSection() {
  const [active, setActive] = React.useState(0);
  const activePillar = PILLARS[active];

  return (
    <Section id="penta-ps" ariaLabel="The Expanded Penta P's Framework" background="muted" pattern>
      <SectionHeader
        icon={Network}
        overline="The Framework"
        overlineClassName="text-amber-gold"
        pillClassName="glass-tinted-amber-gold border-amber-gold/20"
        title={
          <>
            The Expanded Penta P&apos;s. <br />
            <span className="text-gradient-premium">Five Interconnected Pillars.</span>
          </>
        }
        subtitle="Forged through years of operating at the highest levels — where conventional approaches fail. A closed-loop system where insight from one domain strengthens execution in every other."
      />

      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-10 items-center">
        <div className="relative aspect-square max-w-md mx-auto w-full">
          <div className="absolute inset-0 flex items-center justify-center">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              whileInView={{ scale: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="size-32 rounded-full bg-gradient-sovereign flex items-center justify-center shadow-2xl shadow-red/40 relative z-elevated"
            >
              <div className="text-center text-white">
                <div className="text-overline">Closed</div>
                <div className="text-heading-md">Loop</div>
              </div>
              <div className="absolute inset-0 rounded-full bg-gradient-sovereign opacity-30 blur-2xl animate-glow" />
            </motion.div>
          </div>

          {PILLARS.map((p, i) => {
            const radius = 42;
            const x = Math.round((50 + radius * Math.cos((p.angle - 90) * (Math.PI / 180))) * 1000) / 1000;
            const y = Math.round((50 + radius * Math.sin((p.angle - 90) * (Math.PI / 180))) * 1000) / 1000;
            return (
              <motion.button
                key={p.name}
                onClick={() => setActive(i)}
                initial={{ scale: 0, opacity: 0 }}
                whileInView={{ scale: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`absolute size-20 -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-br ${p.gradient} border-2 flex flex-col items-center justify-center transition-all hover:scale-110 ${
                  active === i ? "border-current shadow-2xl scale-110" : "border-border/30"
                } ${p.accent}`}
                style={{ left: `${x}%`, top: `${y}%` }}
                aria-label={p.name}
              >
                <p.icon className="size-7" />
                <span className="text-caption font-bold mt-0.5">{p.name.slice(0, 4)}</span>
              </motion.button>
            );
          })}

          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 100 100" fill="none" preserveAspectRatio="none">
            {PILLARS.map((p, i) => {
              const next = PILLARS[(i + 1) % PILLARS.length];
              const x1 = Math.round((50 + 42 * Math.cos((p.angle - 90) * (Math.PI / 180))) * 1000) / 1000;
              const y1 = Math.round((50 + 42 * Math.sin((p.angle - 90) * (Math.PI / 180))) * 1000) / 1000;
              const x2 = Math.round((50 + 42 * Math.cos((next.angle - 90) * (Math.PI / 180))) * 1000) / 1000;
              const y2 = Math.round((50 + 42 * Math.sin((next.angle - 90) * (Math.PI / 180))) * 1000) / 1000;
              return (
                <motion.line
                  key={i}
                  x1={x1} y1={y1} x2={x2} y2={y2}
                  stroke="currentColor"
                  strokeWidth="0.3"
                  strokeDasharray="2 2"
                  className="text-border opacity-50"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.5 + i * 0.1 }}
                />
              );
            })}
          </svg>
        </div>

        <div>
          <motion.div
            key={active}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className={`glass-strong rounded-2xl p-8 gradient-border bg-gradient-to-br ${activePillar.gradient} relative overflow-hidden`}
          >
            <div className="flex items-start gap-4 mb-5">
              <div className="size-16 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-2xl">
                <activePillar.icon className={`size-8 text-white`} />
              </div>
              <div>
                <div className={`text-overline ${activePillar.accent}`}>Pillar 0{active + 1} of 05</div>
                <h3 className="text-display-sm">{activePillar.name}</h3>
              </div>
            </div>
            <p className="text-body-md leading-relaxed mb-5">{activePillar.description}</p>
            <div className="pt-5 border-t border-border/30">
              <div className="text-overline text-muted-foreground mb-2">Core capabilities</div>
              <div className="flex flex-wrap gap-2">
                {activePillar.capabilities.map((p) => (
                  <span key={p} className={`text-caption px-3 py-1.5 rounded-md bg-background/60 border border-current/20 font-semibold ${activePillar.accent}`}>
                    {p}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>

          <div className="mt-4 flex flex-wrap gap-2">
            {PILLARS.map((p, i) => (
              <button
                key={p.name}
                onClick={() => setActive(i)}
                className={`text-caption px-3 py-1.5 rounded-full border transition-all ${
                  active === i ? "bg-current/20 border-current font-semibold" : "bg-muted/30 border-border/40 hover:bg-muted/50"
                } ${active === i ? p.accent : "text-muted-foreground"}`}
              >
                {p.name}
              </button>
            ))}
          </div>

          <p className="mt-6 text-body-sm text-muted-foreground leading-relaxed">
            The six intersections between these pillars are where compound capabilities emerge — where perception meets privacy, where politics meets policy, where policing meets perception.{" "}
            <span className="text-gradient-premium font-semibold">The integration is the value.</span>
          </p>
        </div>
      </div>
    </Section>
  );
}
